import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import api from "@/client/api/axios";


interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl?: string | null;
  [key: string]: any;
}


function isTokenExpiredSoon(token: string, bufferSeconds = 300): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload?.exp;
    if (typeof exp !== "number") return true;
    return exp * 1000 < Date.now() + bufferSeconds * 1000;
  } catch {
    return true;
  }
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  signup: (
    userData: any,
  ) => Promise<{ requiresVerification: boolean; userId?: string }>;
  signupVendor: (
    userData: any,
  ) => Promise<{ requiresVerification: boolean; userId?: string; email?: string }>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  refreshAccessToken: () => Promise<boolean>;
  getValidAccessToken: () => Promise<string | null>;
  checkAuth: () => Promise<void>;
}


export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        isLoading: false,
        error: null,

        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await api.post("/auth/login", { email, password });
            const { user, access_token, refresh_token } = response.data.data;
            set({
              user,
              accessToken: access_token ?? null,
              refreshToken: refresh_token ?? null,
              isLoading: false,
            });
          } catch (error: any) {
            set({
              error: error.response?.data?.error?.message || "Login failed",
              isLoading: false,
            });
            throw error;
          }
        },

        signup: async (userData: any) => {
          set({ isLoading: true, error: null });
          try {
            const response = await api.post("/auth/register", userData);
            const data = response.data.data;
            set({ isLoading: false });
            return {
              requiresVerification: data.requires_verification,
              userId: data.user.id,
            };
          } catch (error: any) {
            set({
              error: error.response?.data?.error?.message || "Signup failed",
              isLoading: false,
            });
            throw error;
          }
        },

        signupVendor: async (userData: any) => {
          set({ isLoading: true, error: null });
          try {
            const response = await api.post("/auth/vendor-register", userData);
            const data = response.data.data;
            set({ isLoading: false });
            return {
              requiresVerification: Boolean(data?.requires_verification),
              userId: data?.user?.id,
              email: data?.user?.email,
            };
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error || "Vendor registration failed";
            set({
              error: errorMessage,
              isLoading: false,
            });
            throw error;
          }
        },

        logout: async () => {
          set({ isLoading: true });
          try {
            await api.post("/auth/logout");
          } catch {
            // ignore
          } finally {
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isLoading: false,
              error: null,
            });
          }
        },

        refreshAccessToken: async () => {
          const { refreshToken: rt } = get();
          if (!rt) return false;
          try {
            const base = typeof window !== "undefined" ? window.location.origin : "";
            const res = await fetch(`${base}/api/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refresh_token: rt }),
              credentials: "include",
            });
            const data = await res.json();
            if (res.ok && data?.success && data?.data?.access_token) {
              set({ accessToken: data.data.access_token });
              return true;
            }
          } catch {
            // ignore
          }
          set({ user: null, accessToken: null, refreshToken: null });
          return false;
        },

        getValidAccessToken: async () => {
          const { accessToken, refreshAccessToken: refresh } = get();
          if (accessToken && !isTokenExpiredSoon(accessToken)) {
            return accessToken;
          }
          if (await refresh()) {
            return get().accessToken;
          }
          return null;
        },

        setUser: (user: User | null) => set({ user }),

        setAccessToken: (token: string | null) => set({ accessToken: token }),

        checkAuth: async () => {
          set({ isLoading: true });
          try {
            const response = await api.get("/auth/me");
            const user = response.data.data.user;
            set((s) => ({ user, accessToken: s.accessToken ?? null, isLoading: false }));
          } catch (error) {
            
            set({ user: null, accessToken: null, isLoading: false });
          }
        },
      }),
      {
        name: "auth-storage",
        partialize: (state: AuthState) =>
          ({
            user: state.user,
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
          }) as any,
      },
    ),
  ),
);
